import * as React from 'react';
import './style.css';

import { default as Leaderboard, decorateRating } from 'src/ui/Leaderboard';
import AddGame from 'src/ui/AddGame';
import GameRow from 'src/ui/GameRow';
import * as GameRowDialog from 'src/ui/GameRow/Dialog';
import * as PlayerSelectorDialog from 'src/ui/PlayerSelector/Dialog';
import * as RoomSettingsDialog from 'src/ui/Room/RoomSettingsDialog';
import * as RoomSelectorDialog from 'src/ui/Room/RoomSelectorDialog';
import Section from 'src/ui/Section';

import {
  assert,
  Ref,
  RefSet,
  RefMap,
  swap,
  TwinPair,
  notNull,
} from 'src/util';

import App from 'src/state/App';
import Room from 'src/state/Room';
import RoomPage from 'src/state/RoomPage';
import Player from 'src/state/Player';

// TODO: naming...
const component: React.SFC<{
  store: App.Store,
  room: Room,
  page: RoomPage,
}> = (
  ({store, room, page}) => {
    const app = store.getState();

    function getName(playerRef: Ref<Player>) {
      const player = RefSet.resolve(room.entities.players, playerRef);
      return player && player.name;
    }

    const games = Room.Games(room);

    return (
      <div className={`room${page.dialog.hidden ? '' : ' has-dialog'}`}>
        <div className="non-dialog">
          <div
            className="header"
            onClick={() => RoomPage.showDialog(
              {
                type: 'RoomSettings',
                data: '',
              },
              store.dispatchCurrentRoomPage,
            )}
          >
            <div
              className="header-menu-icon"
              onClick={e => {
                e.stopPropagation();

                RoomPage.showDialog(
                  {
                    type: 'RoomSelector',
                    data: {
                      selection: {
                        type: 'existing',
                        data: room.ref.id,
                      },
                      newOptionText: '',
                    },
                    metadata: {},
                  },
                  store.dispatchCurrentRoomPage,
                );
              }}
            >
              <div>
                &#x2630;
              </div>
            </div>
            {
              room.name === null ?
              (<div className="placeholder-text">
                Set Room Name
              </div>) :
              (room.name)
            }
          </div>
          <div className="body">
            <AddGame store={store} room={room} page={page}/>
            <Section
              store={store}
              page={page}
              title="Leaderboard"
              bodyPadding="0"
            >
              <Leaderboard
                players={(() => {
                  const ratings = page.animatedRatings;

                  const previousRoom = App.PreviousRoom(app, room.ref);

                  const previousRatings = previousRoom === null ? null : (
                    Room.Ratings(previousRoom, app.calculator)
                  );

                  // TODO: need to get last available target ratings
                  const targetRatings = Room.Ratings(room, app.calculator);

                  const players = (RefMap
                    .entries(ratings)
                    .map(([playerRef, rating]) => {
                      const player = RefSet.resolve(
                        room.entities.players,
                        playerRef
                      );

                      if (player === null) {
                        assert(false);
                        return null;
                      }

                      const previousRating = previousRatings && RefMap.get(
                        previousRatings,
                        playerRef,
                      );

                      const targetRating = targetRatings && RefMap.get(
                        targetRatings,
                        playerRef,
                      );

                      const decoration = (
                        previousRating !== null &&
                        targetRating !== null &&
                        decorateRating(targetRating, previousRating)
                      ) || null;

                      return {
                        name: player.name,
                        rating,
                        targetRating,
                        decoration,
                      };
                    })
                    .filter(notNull)
                    .sort((a, b) => (
                      // TODO: this is not good... need last target
                      (b.targetRating !== null ? b.targetRating : b.rating) -
                      (a.targetRating !== null ? a.targetRating : a.rating)
                    ))
                  );

                  return players;
                })()}
              />
            </Section>
            <Section
              store={store}
              page={page}
              title="Suggested Games"
              bodyPadding="0"
            >
              {(() => {
                const ratings = page.animatedRatings;

                type SuggestionData = (
                  TwinPair<Ref<Player>> |
                  'loading' |
                  null
                );

                function defenderOnLeft(
                  s: SuggestionData,
                ): SuggestionData {
                  if (!s || s === 'loading') {
                    return s;
                  }

                  function rating(player: Ref<Player>) {
                    return RefMap.get(ratings, player) || 0;
                  }

                  if (rating(s[0]) > rating(s[1])) {
                    return s;
                  }

                  return swap(s);
                }

                const gameSuggestions = Room.SuggestedGames(
                  room,
                  app.calculator,
                );

                const suggestions: SuggestionData[] = (() => {
                  if (gameSuggestions === null) {
                    return [
                      'loading' as 'loading',
                      'loading' as 'loading',
                      'loading' as 'loading',
                    ];
                  }

                  const result: SuggestionData[] = [];

                  for (let i = 0; i < 3; i++) {
                    const suggestion = gameSuggestions[i] || null;
                    result.push(
                      defenderOnLeft(suggestion && suggestion.players)
                    );
                  }

                  return result;
                })();

                return (
                  <div className="suggested-game-rows">
                    {suggestions.map((suggestionData, i) => (
                      <GameRow
                        store={store}
                        room={room}
                        page={page}
                        id={`GameRow-SuggestedGames-${i}`}
                        data={{ type: 'static', players: suggestionData }}
                        alt={i % 2 === 1}
                      />
                    ))}
                  </div>
                );
              })()}
            </Section>
            <Section
              store={store}
              page={page}
              title="History"
              bodyPadding="0"
            >
              {(
                games.length === 0 ?
                (<div className="placeholder-textbox">No games have been added yet.</div>) :
                (<div>
                  <table className="games-table">
                    <tbody>
                      {games.slice().reverse().slice(0, 50).map((g, i) => {
                        const key = (games.length - 1 - i).toString();

                        const vStr = g.outcome === 'draw' ? 'draw' : 'def';

                        const [leftName, rightName] = (
                          g.outcome === 'loss' ?
                          [getName(g.players[1]), getName(g.players[0])] :
                          [getName(g.players[0]), getName(g.players[1])]
                        );

                        return (
                          <tr key={key}>
                            <td className="narrow"/>
                            <td className="wide">{leftName}</td>
                            <td className="v">{vStr}</td>
                            <td className="wide">{rightName}</td>
                            <td
                              className="x narrow"
                              onClick={() => store.dispatchRoom(room.ref, {
                                type: 'remove-game',
                                data: g.ref,
                              })}
                            >
                              x
                            </td>
                          </tr>
                        );
                      })}
                      {games.length <= 50 ? [] : (
                        <tr>
                          <td colSpan={3}>
                            <i>
                              {games.length - 50} more games not displayed.
                            </i>
                          </td>
                        </tr>
                      )}
                      {/* <tr>
                        <td colSpan={5}>
                          <button
                            onClick={() => {
                              store.dispatchRoom(room.ref, {
                                type: 'clear-games',
                              });
                            }}
                          >
                            Clear
                          </button>
                        </td>
                      </tr> */}
                    </tbody>
                  </table>
                </div>)
              )}
            </Section>
            {(() => {
              const pausedPlayers = (RefMap
                .entries(room.paused.players)
                .filter(([player, paused]) => paused)
                .map(([player]) => player)
              );

              if (pausedPlayers.length === 0) {
                return [];
              }

              return (
                <Section
                  store={store}
                  page={page}
                  title="Paused Players"
                  bodyPadding="0"
                >
                  <table className="games-table">
                    <tbody>
                      {pausedPlayers.map((player, i) => (
                        <tr key={i}>
                          <td className="narrow"/>
                          <td className="wide"/>
                          <td className="">{getName(player)}</td>
                          <td className="wide"/>
                          <td
                            className="x narrow"
                            onClick={() => store.dispatchRoom(room.ref, {
                              type: 'set-player-paused',
                              data: {
                                player,
                                paused: false,
                              },
                            })}
                          >
                            x
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>
              );
            })()}
            {(() => {
              if (room.paused.playerPairs.length === 0) {
                return [];
              }

              return (
                <Section
                  store={store}
                  page={page}
                  title="Paused Player Pairs"
                  bodyPadding="0"
                >
                  <table className="games-table">
                    <tbody>
                      {room.paused.playerPairs.slice().reverse().map((pair, i) => {
                        const [leftName, rightName] = [getName(pair[1]), getName(pair[0])];

                        return (
                          <tr key={i}>
                            <td className="narrow"/>
                            <td className="wide">{leftName}</td>
                            <td className="v">v</td>
                            <td className="wide">{rightName}</td>
                            <td
                              className="x narrow"
                              onClick={() => store.dispatchRoom(room.ref, {
                                type: 'set-player-pair-paused',
                                data: {
                                  playerPair: pair,
                                  paused: false,
                                },
                              })}
                            >
                              x
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Section>
              );
            })()}
            <Section
              store={store}
              page={page}
              title="Share"
              bodyPadding="0"
            >
              <div style={{textAlign: 'center'}}>
                <div><canvas id="qr-canvas"/></div>
                <button
                  onClick={() => {
                    let csvStr = 'Player1,Player2,Outcome\n';

                    function getPlayerName(playerRef: Ref<Player>): string {
                      const player = RefSet.resolve(room.entities.players, playerRef);

                      if (!player) {
                        return '(unnamed player)';
                      }

                      return player.name;
                    }

                    for (const game of Room.Games(room)) {
                      csvStr += `${getPlayerName(game.players[0])},${getPlayerName(game.players[0])},${game.outcome}\n`;
                    }

                    const blob = new Blob([csvStr], {type: 'text/csv'});
                    const blobUrl: string = URL.createObjectURL(blob);

                    const aTag = document.createElement('a');
                    aTag.setAttribute('href', blobUrl);
                    aTag.setAttribute('download', `${room.name || 'unnamed-room'}.csv`);
                    aTag.click();

                    setTimeout(
                      () => { URL.revokeObjectURL(blobUrl); },
                      30000,
                    );
                  }}
                >
                  Download CSV
                </button>
              </div>
            </Section>
            { !page.simulator ? [] : [(
              <Section store={store} page={page} title="Simulator">
                <div className="simulator-buttons" style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => {
                      const sim = page.simulator;

                      if (sim) {
                        store.dispatchCurrentRoomPage({
                          type: 'set-simulator',
                          data: { ...sim,
                            queuedGames: sim.queuedGames + 1,
                          },
                        });
                      }
                    }}
                  >
                    Add Game
                  </button>
                </div>
                <div style={{ fontSize: '0.5em' }}>
                  <Leaderboard
                    players={RefMap
                      .entries(page.simulator.hiddenRatings)
                      .map(([playerRef, rating]) => {
                        const player = RefSet.resolve(
                          room.entities.players,
                          playerRef
                        );

                        if (player === null) {
                          return null;
                        }

                        return {
                          name: player.name,
                          rating,
                          decoration: null,
                        };
                      })
                      .filter(notNull)
                      .sort((a, b) => b.rating - a.rating)
                    }
                  />
                </div>
              </Section>
            )] }
          </div>
        </div>
        {(() => {
          if (!page.dialog.state) {
            return [];
          }

          switch (page.dialog.state.type) {
            case 'PlayerSelector': {
              return (
                <PlayerSelectorDialog.Component
                  store={store}
                  room={room}
                  page={page}
                  localState={page.dialog.state}
                />
              );
            }

            case 'GameRow': {
              return (
                <GameRowDialog.Component
                  store={store}
                  room={room}
                  hidden={page.dialog.hidden}
                  localState={page.dialog.state}
                />
              );
            }

            case 'RoomSettings': {
              return (
                <RoomSettingsDialog.Component
                  store={store}
                  page={page}
                  localState={page.dialog.state}
                />
              );
            }

            case 'RoomSelector': {
              return (
                <RoomSelectorDialog.Component
                  store={store}
                  page={page}
                  localState={page.dialog.state}
                  roomHints={App.RoomHints(app)}
                />
              );
            }
          }
        })()}
      </div>
    );
  }
);

export default component;



// WEBPACK FOOTER //
// ./src/ui/Room/index.tsx